import { useState, useEffect } from 'react';

interface UserLocation {
  lat: number;
  lng: number;
}

interface UseUserLocationResult {
  userLocation: UserLocation | null;
  locationError: string | null;
}

export function useUserLocation(): UseUserLocationResult {
  const [userLocation, setUserLocation] = useState<UserLocation | null>(null);
  const [locationError, setLocationError] = useState<string | null>(null);

  useEffect(() => {
    let watchId: number | null = null;
    let isMounted = true;

    const handlePositionSuccess = (position: GeolocationPosition) => {
      if (!isMounted) return;
      const { latitude, longitude } = position.coords;
      console.log(`User location update: ${latitude}, ${longitude}`);
      setUserLocation({ lat: latitude, lng: longitude });
      setLocationError(null); // Clear error on success
    };

    const handlePositionError = (error: GeolocationPositionError) => {
      if (!isMounted) return;
      console.error('Geolocation error:', error);
      setLocationError(`Geolocation Error: ${error.message}`);
      // No fallback to simulated location
    };

    const getLocation = () => {
      if (!navigator.geolocation) {
        setLocationError('Geolocation is not supported by this browser.');
        return;
      }

      // Get current position once for initial load
      navigator.geolocation.getCurrentPosition(
        handlePositionSuccess,
        handlePositionError,
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 } 
      );

      // Always watch position now
      watchId = navigator.geolocation.watchPosition(
        handlePositionSuccess,
        handlePositionError,
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 1000 } // tighter maximumAge for watch
      );
      console.log('Started watching user location.');
    };

    getLocation();

    // Cleanup
    return () => {
      isMounted = false;
      if (watchId !== null) {
        navigator.geolocation.clearWatch(watchId);
        console.log('Stopped watching user location.');
      }
    };
  }, []); // Empty dependency array, runs once on mount

  return { userLocation, locationError };
} 